// Stealth keyword. Pure. No React, no Three.js.
//
// ── Codex (Stealth) ──
//
//   "Keyword Ability. Minions with Stealth cannot be targeted by
//   spells or abilities from your opponents (including attacks),
//   their attacks cannot be defended, they cannot be intercepted,
//   and projectiles cannot hit them. Stealth is tracked with a
//   stealth token. Stealth is lost after the minion interacts with
//   the realm. Non-minions cannot have Stealth. A minion with
//   the Stealth keyword enters the realm with a Stealth token. If a
//   minion becomes disabled or silenced, it will lose its Stealth
//   token, since Stealth is an ability (similar to Ward)."
//
// ── State model (mirrors Ward. V1.27) ──
//
//   • Active Stealth TOKEN lives in `card.statuses` only.
//     `_printedKeywords` is the SIGNAL to grant the token on entry,
//     not the active protection. Because the printed keyword also feeds
//     `getEffectiveStatuses` (badge + smoke), consume must SUPPRESS it
//     and grant must UN-suppress it, otherwise the display re-grants
//     Stealth straight back from the printed source.
//   • Granted idempotently. Re-entering the realm (e.g. via bounce
//     + replay) refreshes the token.
//   • Disabled minions effectively lose the token even if it's still
//     in their statuses (hasActiveStealth guards on isDisabled).
//   • Non-Minions never get a Stealth token (codex: "Non-minions
//     cannot have Stealth").
//
// ── Loss-on-interaction ──
//
// Per codex Stealth is lost "after the minion interacts with the
// realm." Attacks, dealing damage, casting a spell, activating an
// ability. The orchestrator (GameBoard) calls `consumeStealth` after
// each of those resolutions, mirroring the Ward damage-prevention
// consume pattern.

import { isDisabled } from '../automation/scope';
import { isCardSilenced } from './keywordQuery';
import { countsAsMinion } from './typeline';

const STEALTH = 'stealth';

/** True iff the card currently has an active Stealth token. */
export function hasActiveStealth(card) {
  if (!card) return false;
  // Disabled / silenced minions lose Stealth (codex: "If a minion
  // becomes disabled or silenced, it will lose its Stealth token").
  // The mask covers the aura-disable / aura-silence paths that never
  // route through loseStealthIfDisabledOrSilenced's token removal.
  if (isDisabled(card) || isCardSilenced(card)) return false;
  return (card.statuses || []).includes(STEALTH);
}

/** True iff this card SHOULD get a Stealth token on realm entry. */
export function shouldGrantStealthOnEntry(card) {
  if (!card) return false;
  if (!countsAsMinion(card)) return false;
  // The printed keyword is the entry trigger. Aura-granted Stealth
  // is a future extension. Defer to keep this MVP scope tight.
  return (card._printedKeywords || []).includes(STEALTH);
}

/**
 * Add a Stealth token to the card. Idempotent. Re-call on entry
 * (e.g. after a bounce + replay) refreshes without duplication.
 * Returns true when a token was actually added, false when one
 * was already present.
 */
export function grantStealthOnEntry(card) {
  if (!shouldGrantStealthOnEntry(card)) return false;
  // A prior consume suppressed the printed keyword (see consumeStealth);
  // re-entry lifts that so the fresh token shows in the effective-status
  // union (badge + smoke) again.
  if (card._suppressedKeywords?.includes(STEALTH)) {
    card._suppressedKeywords = card._suppressedKeywords.filter((s) => s !== STEALTH);
  }
  const existing = card.statuses || [];
  if (existing.includes(STEALTH)) return false;
  card.statuses = [...existing, STEALTH];
  return true;
}

/**
 * Remove the Stealth token from this card. Returns true when a
 * token was actually consumed, so the orchestrator can decide
 * whether to broadcast / rebuild status badges.
 */
export function consumeStealth(card) {
  if (!card?.statuses?.includes(STEALTH)) return false;
  card.statuses = card.statuses.filter((s) => s !== STEALTH);
  // The token lives in card.statuses, but Stealth is also a printed
  // keyword, so getEffectiveStatuses (the badge, the smoke 3D effect,
  // the hover overlay) would re-grant it straight back from
  // _printedKeywords. Suppress it. Mirroring applyStatusToggle(…, false)
  // So the display agrees with the token-based hasActiveStealth and the
  // unit visibly loses Stealth after it interacts with the realm.
  const suppressed = card._suppressedKeywords || [];
  const printedOrAura = (card._printedKeywords || []).includes(STEALTH)
    || (card._auraStatuses || []).includes(STEALTH);
  if (printedOrAura && !suppressed.includes(STEALTH)) {
    card._suppressedKeywords = [...suppressed, STEALTH];
  }
  return true;
}

/**
 * Codex Stealth: "If a minion becomes disabled or silenced, it will
 * lose its Stealth token, since Stealth is an ability." Call after a
 * status change settles. State-based (not keyed off the triggering
 * status) so it's correct however the disable / silence arrived, and
 * permanent. The token stays gone after the disable / silence ends.
 * Returns true when a token was actually removed, so the host can
 * rebuild the badge / broadcast the loss.
 */
export function loseStealthIfDisabledOrSilenced(card) {
  if (!card) return false;
  if (!isDisabled(card) && !isCardSilenced(card)) return false;
  return consumeStealth(card);
}
